import { useEffect, useState, useCallback, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Box, Typography, IconButton, CircularProgress, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import useAuth from '../../hoc/AuthProvider/useAuth';
import { Permissions } from '../../config/permissions';
import SaveIcon from '@mui/icons-material/Save';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { ApexOptions } from 'apexcharts';
import { Widget, ChartSeries, TimeRange, ChartOptions, ChartType } from '../../types/dashboard';
import { getChartData } from '../../services/dashboardService';
import TimeRangeBar from './TimeRangeBar';

const TIME_RANGE_MINUTES: Record<TimeRange, number> = {
  '15m': 15, '1h': 60, '6h': 360, '24h': 1440, '2d': 2880,
};

const BAR_BUCKET_MS: Record<TimeRange, number> = {
  '15m': 5 * 60000,
  '1h':  15 * 60000,
  '6h':  60 * 60000,
  '24h': 4 * 3600000,
  '2d':  8 * 3600000,
};

const aggregateByInterval = (data: [number, number][], bucketMs: number): [number, number][] => {
  const buckets: Record<number, number[]> = {};
  data.forEach(([ts, val]) => {
    const b = Math.floor(ts / bucketMs) * bucketMs;
    if (!buckets[b]) buckets[b] = [];
    buckets[b].push(val);
  });
  return Object.entries(buckets)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([b, vals]) => [
      Number(b),
      parseFloat((vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(2)),
    ]);
};


const HEATMAP_BUCKET_MS: Record<TimeRange, number> = {
  '15m': 60000,
  '1h':  5 * 60000,
  '6h':  30 * 60000,
  '24h': 60 * 60000,
  '2d':  2 * 3600000,
};

const aggregateForHeatmap = (
  data: [number, number][],
  timeRange: TimeRange,
  from: Date,
  to: Date,
): { x: string; y: number | null }[] => {
  const bucketMs = HEATMAP_BUCKET_MS[timeRange];

  const buckets: Record<number, number[]> = {};
  data.forEach(([ts, val]) => {
    const b = Math.floor(ts / bucketMs) * bucketMs;
    if (!buckets[b]) buckets[b] = [];
    buckets[b].push(val);
  });

  const startBucket = Math.floor(from.getTime() / bucketMs) * bucketMs;
  const endBucket = Math.floor(to.getTime() / bucketMs) * bucketMs;

  const result: { x: string; y: number | null }[] = [];
  for (let b = startBucket; b <= endBucket; b += bucketMs) {
    const d = new Date(b);
    const label = timeRange === '2d'
      ? d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }) + ' ' +
        d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    const vals = buckets[b];
    result.push({
      x: label,
      y: vals ? parseFloat((vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(2)) : null,
    });
  }
  return result;
};


const transformSeries = (series: ChartSeries[], type: ChartType, timeRange: TimeRange = '1h', from?: Date, to?: Date, chartOptions?: ChartOptions): any => {
  const latest = (s: ChartSeries) => s.data.length > 0 ? s.data[s.data.length - 1][1] : 0;

  switch (type) {
    // Son değeri 0-100'e kırparak ark dolumu olarak kullan
    case 'radialBar':
      if (series.length === 0) return [0];
      return series.map(s => parseFloat(Math.min(100, Math.max(0, latest(s))).toFixed(1)));

    // Zaman aralığına göre sabit bucket'lara böl, boş slotlar null
    case 'heatmap': {
      if (series.length === 0) return [];
      const heatFrom = from ?? new Date(Date.now() - TIME_RANGE_MINUTES[timeRange] * 60000);
      const heatTo = to ?? new Date();
      return series.map(s => ({
        name: s.name,
        data: aggregateForHeatmap(s.data, timeRange, heatFrom, heatTo),
      }));
    }

    case 'bar':
      if (series.length === 0) return [];
      if (chartOptions?.horizontal) {
        if (chartOptions.timeGrouped) {
          // Grouped Bar: zaman dilimleri Y ekseninde, key'ler yan yana
          const bucketMs = BAR_BUCKET_MS[timeRange];
          return series.map(s => ({
            name: s.name,
            data: aggregateByInterval(s.data, bucketMs).map(([ts, val]: [number, number]) => ({
              x: timeRange === '2d'
                ? new Date(ts).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }) + ' ' +
                  new Date(ts).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
                : new Date(ts).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
              y: val,
            })),
          }));
        }
        // Basic Bar: her key için tek çubuk, son değer
        return [{ name: 'Değer', data: series.map(s => ({ x: s.name, y: parseFloat(Math.max(0, latest(s)).toFixed(2)) })) }];
      }
      // Dikey sütun: değişken bucket, datetime ekseninde
      return series.map(s => ({ name: s.name, data: aggregateByInterval(s.data, BAR_BUCKET_MS[timeRange]) }));

    // pie / donut: her serinin son değerini düz sayı dizisine dönüştür
    case 'pie':
    case 'donut':
      if (series.length === 0) return [0];
      return series.map(s => parseFloat(Math.max(0, latest(s)).toFixed(2)));


    // line / area: ham veriyi olduğu gibi geçir, ApexCharts zaman ekseninde çizer
    default:
      if (series.length === 0) return [];
      return series.map(s => ({
        name: s.name,
        data: s.data.map(([ts, val]) => [ts, parseFloat(val.toFixed(2))]),
      }));
  }
};

const buildApexOptions = (
  widget: Widget,
  darkMode: boolean,
  from: Date,
  to: Date,
  series: ChartSeries[],
): ApexOptions => {
  const o: ChartOptions = widget.chartOptions ?? {};
  const labels = (widget.type === 'radialBar' || widget.type === 'pie' || widget.type === 'donut')
    ? series.map(s => s.name)
    : undefined;
  const dm = darkMode;
  const gridColor = dm ? '#1e3a5f' : '#e2e8f0';
  const labelColor = dm ? '#94a3b8' : '#64748b';
  const textColor = dm ? '#f1f5f9' : '#1e293b';

  const base: ApexOptions = {
    chart: {
      id: `chart-${widget.id}`,
      type: widget.type as any,
      background: 'transparent',
      stacked: o.stacked ?? false,
      animations: { enabled: o.animations ?? true, dynamicAnimation: { enabled: true, speed: 600 } },
      toolbar: { show: true },
    },
    theme: { mode: dm ? 'dark' : 'light' },
    colors: o.paletteColors,
    dataLabels: { enabled: o.dataLabels ?? false },
    legend: { show: true, position: 'top', labels: { colors: labelColor } },
    noData: { text: 'Veri bekleniyor...' },
    ...(labels ? { labels } : {}),
  };

  // ── Line ──────────────────────────────────────────────
  if (widget.type === 'line') {
    return {
      ...base,
      stroke: { curve: o.strokeCurve ?? 'smooth', width: o.strokeWidth ?? 3 },
      fill: { type: 'solid' },
      markers: { size: 0, hover: { size: 6 } },
      xaxis: { type: 'datetime', min: from.getTime(), max: to.getTime(), labels: { datetimeUTC: false, style: { colors: labelColor } }, axisBorder: { show: false }, axisTicks: { show: false } },
      yaxis: { labels: { style: { colors: labelColor } } },
      grid: { borderColor: gridColor, strokeDashArray: 5, xaxis: { lines: { show: false } } },
      tooltip: { x: { format: 'dd MMM HH:mm' }, theme: dm ? 'dark' : 'light' },
      chart: { ...base.chart, dropShadow: { enabled: true, top: 3, left: 0, blur: 4, opacity: 0.15 } },
    };
  }

  // ── Area ──────────────────────────────────────────────
  if (widget.type === 'area') {
    return {
      ...base,
      stroke: { curve: o.strokeCurve ?? 'smooth', width: o.strokeWidth ?? 2 },
      fill: {
        type: 'gradient',
        gradient: { shadeIntensity: 1, opacityFrom: 0.6, opacityTo: 0.05, stops: [0, 95, 100] },
      },
      markers: { size: 0, hover: { size: 5 } },
      xaxis: { type: 'datetime', min: from.getTime(), max: to.getTime(), labels: { datetimeUTC: false, style: { colors: labelColor } }, axisBorder: { show: false }, axisTicks: { show: false } },
      yaxis: { labels: { style: { colors: labelColor } } },
      grid: { borderColor: gridColor, strokeDashArray: 5, xaxis: { lines: { show: false } } },
      tooltip: { x: { format: 'dd MMM HH:mm' }, theme: dm ? 'dark' : 'light' },
    };
  }

  // ── Bar (yatay) ───────────────────────────────────────
  if (widget.type === 'bar' && o.horizontal) {
    return {
      ...base,
      plotOptions: { bar: { horizontal: true, borderRadius: 6, barHeight: '55%' } },
      dataLabels: { enabled: o.dataLabels ?? true, style: { colors: [textColor] } },
      xaxis: { labels: { style: { colors: labelColor } }, axisBorder: { show: false }, axisTicks: { show: false } },
      yaxis: { labels: { style: { colors: labelColor } } },
      grid: { borderColor: gridColor, strokeDashArray: 5, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
      tooltip: { theme: dm ? 'dark' : 'light' },
      fill: { type: 'gradient', gradient: { shade: 'light', type: 'horizontal', shadeIntensity: 0.25, opacityFrom: 0.85, opacityTo: 0.85 } },
    };
  }

  // ── Bar (dikey sütun) ─────────────────────────────────
  if (widget.type === 'bar') {
    return {
      ...base,
      plotOptions: { bar: { horizontal: false, borderRadius: 6, columnWidth: '55%' } },
      dataLabels: { enabled: false },
      xaxis: { type: 'datetime', min: from.getTime(), max: to.getTime(), labels: { datetimeUTC: false, format: 'HH:mm', style: { colors: labelColor } }, axisBorder: { show: false }, axisTicks: { show: false } },
      yaxis: { labels: { style: { colors: labelColor } } },
      grid: { borderColor: gridColor, strokeDashArray: 5, xaxis: { lines: { show: false } } },
      tooltip: { x: { format: 'dd MMM HH:mm' }, theme: dm ? 'dark' : 'light' },
      fill: { type: 'gradient', gradient: { shade: 'light', type: 'vertical', shadeIntensity: 0.25, gradientToColors: undefined, inverseColors: true, opacityFrom: 0.85, opacityTo: 0.85, stops: [50, 0, 100, 100] } },
    };
  }

  // ── RadialBar Semicircle ──────────────────────────────
  if (widget.type === 'radialBar' && o.semicircle) {
    return {
      ...base,
      chart: { ...base.chart, offsetY: -30 },
      plotOptions: {
        radialBar: {
          startAngle: -90,
          endAngle: 90,
          track: {
            background: dm ? '#334155' : '#e7e7e7',
            strokeWidth: '97%',
            margin: 5,
            dropShadow: { enabled: true, top: 2, left: 0, color: '#444', opacity: 0.5, blur: 2 },
          },
          dataLabels: {
            name: { show: false },
            value: {
              offsetY: -25,
              fontSize: '28px',
              fontWeight: 700,
              color: textColor,
              formatter: (_val: any) => String(parseFloat(Number(_val).toFixed(1))),
            },
          },
        },
      },
      grid: { padding: { top: 0, bottom: -70, left: 0, right: 0 } },
      fill: {
        type: 'gradient',
        gradient: { shade: 'light', shadeIntensity: 0.4, inverseColors: false, opacityFrom: 1, opacityTo: 1, stops: [0, 50, 53, 91] },
      },
    };
  }

  // ── RadialBar Gauge ────────────────────────────────────
  if (widget.type === 'radialBar') {
    return {
      ...base,
      plotOptions: {
        radialBar: {
          startAngle: -140,
          endAngle: 140,
          hollow: { size: '60%', background: 'transparent' },
          track: {
            background: dm ? '#334155' : '#e2e8f0',
            strokeWidth: '100%',
            margin: 4,
          },
          dataLabels: {
            name: { show: true, fontSize: '14px', color: labelColor, fontWeight: 400, offsetY: -8 },
            value: {
              show: true,
              fontSize: '32px',
              fontWeight: 700,
              color: textColor,
              offsetY: 8,
              formatter: (_val: any) => String(parseFloat(Number(_val).toFixed(1))),
            },
            ...(series.length > 1 ? {
              total: {
                show: true,
                label: 'Ortalama',
                color: labelColor,
                fontSize: '14px',
                formatter: (w: any) => {
                  const vals: number[] = w.globals.seriesTotals ?? [];
                  const avg = vals.reduce((a: number, b: number) => a + b, 0) / Math.max(vals.length, 1);
                  return avg.toFixed(1);
                },
              },
            } : {}),
          },
        },
      },
      fill: {
        type: 'gradient',
        gradient: { shade: 'dark', shadeIntensity: 0.2, inverseColors: false, opacityFrom: 1, opacityTo: 1, stops: [0, 60, 100] },
      },
      stroke: { lineCap: 'round' },
    };
  }

  // ── Heatmap ───────────────────────────────────────────
  if (widget.type === 'heatmap') {
    return {
      ...base,
      plotOptions: { heatmap: { shadeIntensity: 0.65, radius: 2, enableShades: true } },
      xaxis: { labels: { rotate: -45, hideOverlappingLabels: true, style: { colors: labelColor, fontSize: '9px' } }, axisBorder: { show: false } },
      yaxis: { labels: { style: { colors: labelColor, fontSize: '10px' } } },
      grid: { borderColor: gridColor, padding: { right: 40 } },
      tooltip: { theme: dm ? 'dark' : 'light' },
      dataLabels: { enabled: false },
    };
  }

  // ── Pie / Donut ───────────────────────────────────────
  if (widget.type === 'pie' || widget.type === 'donut') {
    return {
      ...base,
      plotOptions: { pie: { donut: { size: widget.type === 'donut' ? '60%' : '0%', labels: { show: widget.type === 'donut', total: { show: true, label: 'Toplam', color: labelColor, fontSize: '14px', fontWeight: 700 } } } } },
      dataLabels: { enabled: true, style: { fontSize: '12px', colors: ['#fff'] }, dropShadow: { enabled: false } },
      legend: { show: true, position: 'bottom', labels: { colors: labelColor } },
      tooltip: { theme: dm ? 'dark' : 'light' },
    };
  }

  // Buraya düşmemeli; TypeScript exhaustive check için
  return base;
};

const POLL_INTERVAL_MS = 30000;

interface Props {
  widget: Widget;
  darkMode: boolean;
  isDirty: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLElement>;
  onEdit: () => void;
  onRemove: () => void;
  onSave: () => void;
  onTimeRangeChange: (range: TimeRange) => void;
}

const ChartWidget = ({ widget, darkMode, isDirty, dragHandleProps, onEdit, onRemove, onSave, onTimeRangeChange }: Props) => {
  const { havePermission } = useAuth();
  const canEdit   = havePermission(Permissions.Dashboard_Update);
  const canDelete = havePermission(Permissions.Dashboard_Delete);
  const [rawSeries, setRawSeries] = useState<ChartSeries[]>([]);
  const [loading, setLoading] = useState(false);
  const [rangeWindow, setRangeWindow] = useState<{ from: Date; to: Date }>(() => {
    const to = new Date();
    const from = new Date(to.getTime() - TIME_RANGE_MINUTES[widget.timeRange] * 60000);
    return { from, to };
  });

  const fetchData = useCallback(async () => {
    if (!widget.deviceId || (widget.keyIds ?? []).length === 0) return;
    setLoading(true);
    const to = new Date();
    const from = new Date(to.getTime() - TIME_RANGE_MINUTES[widget.timeRange] * 60000);
    setRangeWindow({ from, to });
    try {
      const data = await getChartData(widget.deviceId, widget.keyIds ?? [], from.toISOString(), to.toISOString());
      setRawSeries(data);
    } catch {
      console.error('Grafik verisi alınamadı');
    } finally {
      setLoading(false);
    }
  }, [widget.deviceId, widget.keyIds, widget.timeRange]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  const displaySeries = useMemo(
    () => transformSeries(rawSeries, widget.type, widget.timeRange, rangeWindow.from, rangeWindow.to, widget.chartOptions),
    [rawSeries, widget.type, widget.timeRange, rangeWindow, widget.chartOptions],
  );
  const options = useMemo(
    () => buildApexOptions(widget, darkMode, rangeWindow.from, rangeWindow.to, rawSeries),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [widget.id, widget.type, widget.chartOptions, darkMode, rangeWindow, rawSeries],
  );

  const cardBg = darkMode ? '#1e293b' : '#ffffff';
  const borderColor = darkMode ? '#334155' : '#e2e8f0';
  const subtitleColor = darkMode ? '#94a3b8' : '#64748b';
  const textColor = darkMode ? '#f1f5f9' : '#1e293b';


  return (
    <Box sx={{
      backgroundColor: cardBg,
      border: `1px solid ${borderColor}`,
      borderRadius: 2,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 320,
      overflow: 'hidden',
      transition: 'border-color 0.2s',
      '&:hover': { borderColor: 'primary.main' },
    }}>
      {/* Header */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1, cursor: 'grab', color: subtitleColor, pt: 0.5 }} {...dragHandleProps}>
          <DragIndicatorIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" color={subtitleColor} noWrap>{widget.deviceName || 'Cihaz'}</Typography>
          <Typography variant="h6" fontWeight={700} color={textColor} noWrap>{widget.title}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isDirty && (
            <Tooltip title="Kaydet">
              <IconButton size="small" onClick={onSave} sx={{ color: 'primary.main' }}>
                <SaveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canEdit   && <IconButton size="small" onClick={onEdit}   sx={{ color: subtitleColor }}><EditIcon  fontSize="small" /></IconButton>}
          {canDelete && <IconButton size="small" onClick={onRemove} sx={{ color: subtitleColor }}><CloseIcon fontSize="small" /></IconButton>}
        </Box>
      </Box>

      {/* Chart */}
      <Box sx={{ flex: 1, px: 1, minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {!widget.deviceId ? (
          <Typography color={subtitleColor} variant="body2">Sensör seçmek için ✏️ tıklayın</Typography>
        ) : loading ? (
          <CircularProgress size={28} />
        ) : (
          <Box sx={{ width: '100%' }}>
            <ReactApexChart
              key={`${widget.id}-${widget.type}`}
              type={widget.type as any}
              series={displaySeries}
              options={options}
              height={240}
            />
          </Box>
        )}
      </Box>

      <Box sx={{ px: 2, pb: 1.5, borderTop: `1px solid ${borderColor}`, pt: 1 }}>
        <TimeRangeBar value={widget.timeRange} onChange={onTimeRangeChange} darkMode={darkMode} />
      </Box>
    </Box>
  );
};

export default ChartWidget;
